<template>
  <div id="tabla-acuerdos-comerciales">

    <!-- Filtros sobre las busqueda (Filtros de 2do nivel) -->
    <div class="pre-tabla d-flex align-center" v-if="acuerdos_comerciales.length">

      <!-- Filtrado por ca-filtro -->
      <ca-filtro
        :items_sin_filtrar="acuerdos_comerciales"
        :keys=keys
        :items.sync="acuerdos_comerciales_filtrados_por_ca_filtro"
        :filtro.sync="filtro_tabla"
        placeholder="Filtrar acuerdos"
        :disabled="loading"
        class="mr-6"
      />

      <!-- Filtro por agente de sales -->
      <div class="filtro-acuerdo-comercial--agentes mr-6">
        <v-autocomplete
          :items="usuarios_agente"
          attach
          dense
          item-text="nombre_formateado"
          item-value="valor_original"
          label="Agente de ventas"
          placeholder="Todos"
          no-data-text="No hay agentes disponibles"
          v-model="filtro_agente"
          :disabled="loading"
        ></v-autocomplete>
      </div>

      <!-- Filtro por estado -->
      <div class="filtro-acuerdo-comercial--estados mr-6" v-if="tipo_acuerdo_filtros_primer_nivel !== TIPO_BUSQUEDA_ACUERDO_COMERCIAL_POR_DEFECTO">
        <v-autocomplete
          :items="estados"
          attach
          dense
          item-text="texto"
          item-value="key"
          label="Estados"
          placeholder="Todos"
          no-data-text="No hay estados disponibles"
          v-model="filtro_estado"
          :disabled="loading"
        ></v-autocomplete>
      </div>

      <!-- Chip totalizador de resultados (cambia el wording si estamos en loading de la tabla) -->
      <ca-chip
        disabled
        contentClass="chip-totalizador mr-2"
        :class="{'chip-totalizador--deshabilitado': loading}"
      >
        <template>
          <div class="d-flex justify-center align-center">
            <div>Resultados:</div>

            <!-- Mostramos wording ... en los chips si estamos en loading de tabla -->
            <div v-if="loading" class="chip-totalizador--texto-oscuro">...</div>

            <!-- No estamos en loading de tabla (mostramos los contadores) -->
            <div v-else class="chip-totalizador--texto-oscuro">

              <div v-if="acuerdos_comerciales.length !== this.acuerdosComercialesFiltrados.length">
                <div>{{ acuerdosComercialesFiltrados.length }} de {{ acuerdos_comerciales.length }}</div>
              </div>
              <div v-else>
                <div>{{ acuerdos_comerciales.length }}</div>
              </div>

            </div>
          </div>
        </template>
      </ca-chip>

      <!-- Chip totalizador de UFs -->
      <ca-chip
        v-if="tipo_acuerdo_filtros_primer_nivel !== TIPO_BUSQUEDA_ACUERDO_COMERCIAL_POR_DEFECTO"
        disabled
        contentClass="chip-totalizador"
        :class="{'chip-totalizador--deshabilitado': loading}"
      >
        <template>
          <div class="d-flex justify-center align-center ml-0">
            <div>UFs:</div>

            <!-- Mostramos contador o estado de loading -->
            <div class="chip-totalizador--texto-oscuro">{{ loading ? '...' : chipTotalizadorUfs }}</div>
          </div>
        </template>
      </ca-chip>
    </div>

    <ca-tabla
      :items="acuerdosComercialesActualizados"
      :headers="headersTabla"
      :loading="loading"
      :paginado="acuerdos_comerciales.length > CANTIDAD_ITEMS_POR_PAGINA"
      :items_por_pagina="CANTIDAD_ITEMS_POR_PAGINA"
      loading_text="Buscando acuerdos comerciales..."
      filas_hover
      header_fijo
      filas_clickeables
      scrollable
      class="elevation-4"
    >
      <template slot="item" slot-scope="props">
        <tr @click="abrirSidesheetDetalles(props.item)">

          <!-- Menú de acciones -->
          <td>
            <menu-contextual-tabla-acuerdos-comerciales :acuerdo_comercial="props.item"/>
          </td>

          <!-- Administración -->
          <td :title="props.item.administracion.nombre_con_id_formateado">
            <div class="texto-mostrado-desbordamiento">
              {{ props.item.administracion.nombre_con_id_formateado }}
            </div>
          </td>

          <!-- Consorcio + tipo | Chip administración | Descuentos -->
          <td v-if="props.item.tipo_propuesta === TIPO_ACUERDO_CONSORCIO && tipo_acuerdo_filtros_primer_nivel !== TIPO_BUSQUEDA_ACUERDO_COMERCIAL_POR_DEFECTO" :title="props.item.consorcio.nombre_consorcio_con_tipo_tooltip_formateado">
            <div class="d-flex align-center">
              <div class="material-symbols-outlined icono-tipo-consorcio mr-2">
                {{ props.item.consorcio.tipo === TIPO_CONSORCIO_EDIFICIO ? 'domain' : 'nature' }}
              </div>
              <div class="texto-mostrado-desbordamiento">
                {{ props.item.consorcio.nombre_con_id_formateado }}
              </div>
            </div>
          </td>
          <td v-else-if="tipo_acuerdo_filtros_primer_nivel === TIPO_BUSQUEDA_ACUERDO_COMERCIAL_ADMINISTRACION"  class="td-columna-estado">
            <chip-estado-acuerdo-comercial :estado="ESTADO_ACUERDO_COMERCIAL_ADMINISTRACION"/>
          </td>
          <td v-else> <!-- Búsqueda por defecto -->
            <div>{{ props.item.descuentos_formateados }}</div>
          </td>

          <!-- Cantidad UFs -->
          <td v-if="tipo_acuerdo_filtros_primer_nivel !== TIPO_BUSQUEDA_ACUERDO_COMERCIAL_POR_DEFECTO">
            <div class="text-end pr-4">
              {{ props.item.tipo_propuesta === TIPO_ACUERDO_CONSORCIO ? props.item.consorcio.cantidad_ufs_facturables : '' }}
            </div>
          </td>
          <td v-else></td> <!-- Necesario para ocupar ese espacio en caso de que sea el tipo de búsqueda por defecto -->

          <!-- Chip estado + chip expirado -->
          <td v-if="tipo_acuerdo_filtros_primer_nivel !== TIPO_BUSQUEDA_ACUERDO_COMERCIAL_POR_DEFECTO">
            <div v-if="props.item.tipo_propuesta === TIPO_ACUERDO_CONSORCIO">
              <div class="d-flex align-center">
                <chip-estado-acuerdo-comercial :estado="props.item.estado"/>

                <!-- Si el acuerdo comercial esta expirado mostramos el chip asociado -->
                <chip-estado-acuerdo-comercial
                  v-if="props.item.expirada"
                  :estado="ESTADO_ACUERDO_COMERCIAL_EXPIRADO"
                >
                </chip-estado-acuerdo-comercial>
              </div>
            </div>
          </td>

          <!-- Columna opcional: Fecha de ultima actualización -->
          <td v-if="mostrar_columnas_opcionales && tipo_acuerdo_filtros_primer_nivel !== TIPO_BUSQUEDA_ACUERDO_COMERCIAL_POR_DEFECTO">
            <div v-if="props.item.tipo_propuesta === TIPO_ACUERDO_CONSORCIO && props.item.fecha_edicion">
              {{ props.item.fecha_edicion_formateada }}
            </div>
            <div v-else>{{ props.item.fecha_edicion | guionSiEsNulo }}</div>
          </td>

          <!-- Columna opcional: Días restantes -->
          <td v-if="mostrar_columnas_opcionales && tipo_acuerdo_filtros_primer_nivel !== TIPO_BUSQUEDA_ACUERDO_COMERCIAL_POR_DEFECTO">

            <!-- Hay días restantes -->
            <div v-if="props.item.propuesta_consorcio && props.item.tipo_propuesta === TIPO_ACUERDO_CONSORCIO && props.item.propuesta_consorcio.dias_restantes">
              <div v-if="props.item.propuesta_consorcio.dias_restantes > 0" :class="{'label-dias-restantes-danger': props.item.propuesta_consorcio.dias_restantes < 6}">
                {{ props.item.propuesta_consorcio.dias_restantes_formateado }}
              </div>
            </div>
            <!-- No hay días restantes -->
            <div v-else>-</div>

          </td>

          <!-- Descuentos -->
          <td v-if="tipo_acuerdo_filtros_primer_nivel !== TIPO_BUSQUEDA_ACUERDO_COMERCIAL_POR_DEFECTO" :title="props.item.descuentos_formateados">
            <div class="texto-mostrado-desbordamiento">{{ props.item.descuentos_formateados }}</div>
          </td>

          <!-- Periodo de inicio de facturación -->
          <td v-if="tipo_acuerdo_filtros_primer_nivel !== TIPO_BUSQUEDA_ACUERDO_COMERCIAL_POR_DEFECTO">
            <div>{{ props.item.propuesta_consorcio ? props.item.propuesta_consorcio.periodo_inicio_facturacion_formateado : '' }}</div>
          </td>

          <!-- Usuario sales -->
          <td :title="props.item.usuario_sales">
            <div v-if="props.item.usuario_sales">{{ props.item.usuario_sales | trimUsuarioSales }}</div>
          </td>

          <!-- Fecha de creación -->
          <td>
            <div>{{ props.item.fecha_creacion_formateado }}</div>
          </td>

          <!-- Usuario edición -->
          <td :title="props.item.usuario_edicion">
            <div v-if="props.item.usuario_edicion">{{ props.item.usuario_edicion | trimUsuarioSales }}</div>
          </td>

          <!-- Origen del consorcio -->
          <td v-if="tipo_acuerdo_filtros_primer_nivel !== TIPO_BUSQUEDA_ACUERDO_COMERCIAL_POR_DEFECTO">
            <div>{{ props.item.origen_formateado }}</div>
          </td>

          <!-- Columna de relleno, para completar toda la tabla -->
          <td></td>
        </tr>
      </template>
    </ca-tabla>
  </div>
</template>

<script>
import { trimUsuarioSales } from '../acuerdo-comercial.helpers';
import ChipEstadoAcuerdoComercial from './ChipEstadoAcuerdoComercial.vue';
import MenuContextualTablaAcuerdosComerciales from './MenuContextualTablaAcuerdosComerciales.vue';
import { ESTADOS_FILTRABLES_PARA_TABLA_ACUERDOS_COMERCIALES, TIPO_BUSQUEDA_ACUERDO_COMERCIAL_CONSORCIOS, TIPO_BUSQUEDA_ACUERDO_COMERCIAL_POR_DEFECTO, TIPO_BUSQUEDA_ACUERDO_COMERCIAL_ADMINISTRACION, TiposEstadosAcuerdoComercial, TipoConsorcio, TipoAcuerdo, ESTADO_ACUERDO_COMERCIAL_TERMINADORES } from '../acuerdo-comercial.consts';
import { METADATA_GETTER_USUARIOS_SALES } from '@/store/metadata/metadata.types';
import { EventBus } from '@/main';

/**
 * Componente que muestra la tabla de acuerdos comerciales
 * 
 * La tabla cuenta con headers dinámicos y columnas opcionales:
 *  - Si el tipo de búsqueda es por defecto, se muestran los headers para la búsqueda por defecto
 *  - Si el tipo de búsqueda no es por defecto y el flag para mostrar las columnas opcionales esta activo, se muestran los headers que incluyen las columnas opcionales caso contrario se muestran los headers sin las columnas opcionales
 */
export default {
  name: 'TablaAcuerdosComerciales',
  components: {
    ChipEstadoAcuerdoComercial,
    MenuContextualTablaAcuerdosComerciales
  },
  props: {
    acuerdos_comerciales: {type: Array, default: () => []},
    tipo_acuerdo_filtros_primer_nivel: {type: String, default: TIPO_BUSQUEDA_ACUERDO_COMERCIAL_CONSORCIOS}, // Indica el tipo de acuerdo comercial que se seleccionó en los filtros del tab buscador. El tab de acuerdos en curso no envía esta prop, ya que los tipos de acuerdos están vinculados únicamente al consorcio (por este motivo, se establece por defecto el tipo de acuerdo consorcio)
    loading: {type: Boolean, default: false} // Flag para indicar si se está modo loading la tabla
  },
  data: () => ({

    // Valor de los filtros de segundo nivel
    filtro_agente: null,
    filtro_tabla: '',
    filtro_estado: null,

    // Lista de opciones para los filtros de 2do nivel
    usuarios_agente: [],
    estados: [],

    mostrar_columnas_opcionales: false,

    acuerdos_comerciales_filtrados_por_ca_filtro: [],

    // Constantes
    CANTIDAD_ITEMS_POR_PAGINA: 16,

    TIPO_CONSORCIO_EDIFICIO: TipoConsorcio.EDIFICIO,
    TIPO_ACUERDO_CONSORCIO: TipoAcuerdo.CONSORCIO,
    TIPO_ACUERDO: TipoAcuerdo,

    ESTADO_ACUERDO_COMERCIAL_EXPIRADO: TiposEstadosAcuerdoComercial.EXPIRADO,
    ESTADO_ACUERDO_COMERCIAL_ADMINISTRACION: TiposEstadosAcuerdoComercial.ADMINISTRACION,

    TIPO_BUSQUEDA_ACUERDO_COMERCIAL_POR_DEFECTO: TIPO_BUSQUEDA_ACUERDO_COMERCIAL_POR_DEFECTO,
    TIPO_BUSQUEDA_ACUERDO_COMERCIAL_ADMINISTRACION: TIPO_BUSQUEDA_ACUERDO_COMERCIAL_ADMINISTRACION,

    // Variante para headers de la tabla
    headers_con_columnas_opcionales: [
      { text: '', sortable: false, class:'col-acciones' },
      { text: 'ADMINISTRACIÓN', value: 'administracion.nombre_con_id_formateado', class:'col-administracion' },
      { text: 'CONSORCIO', value: 'consorcio.nombre_consorcio_con_tipo_tooltip_formateado', class:'col-consorcio' },
      { text: 'UFS FACTU.', value: 'consorcio.cantidad_ufs_facturables', class:'col-ufs' },
      { text: 'ESTADO', value: 'nombre_estado_formateado', class:'col-estado' },
      { text: 'ÚLTIMA ACTUALIZA.', value: 'fecha_edicion_formateada', class:'col-ultima-actualizacion header-gris', columna_opcional: true },
      { text: 'RESTAN', value: 'propuesta_consorcio.dias_restantes_formateado', class:'col-restan header-gris', columna_opcional: true },
      { text: 'DESCUENTOS', value: 'descuentos_formateados', class:'col-descuentos' },
      { text: 'INICIO', value: 'propuesta_consorcio.periodo_inicio_facturacion_formateado', class:'col-inicio' },
      { text: 'CREADOR', value: 'usuario_sales', class:'col-creador' },
      { text: 'FECHA', value: 'fecha_creacion_formateado', class:'col-creacion' },
      { text: 'EDITOR', value: 'usuario_edicion', class:'col-editor' },
      { text: 'ORIGEN', value: 'origen_formateado', class:'col-origen' },
      { text: '', value:'', sortable: false, class:''} // Columna necesaria, para expadir el total de la tabla y funcione a resoluciones bajas
    ],
    headers_sin_columnas_opcionales: [],
    headers_busqueda_ac_por_defecto: [
      { text: '', sortable: false, class:'col-acciones' },
      { text: 'ADMINISTRACIÓN', value: 'administracion.nombre_con_id_formateado', class:'col-administracion' },
      { text: 'DESCUENTOS', value: 'descuentos_formateados', class:'col-descuentos' },
      { text: '', value:'', sortable: false, class:'col-relleno'}, // Columna necesaria, para ocupar el espacio de las columnas ocultas
      { text: 'CREADOR', value: 'usuario_sales', class:'col-creador' },
      { text: 'FECHA', value: 'fecha_creacion_formateado', class:'col-creacion' },
      { text: 'EDITOR', value: 'usuario_edicion', class:'col-editor' },
    ],

    // Keys para el filtrado por ca-filtro
    keys: ['administracion.nombre_con_id_formateado', 'consorcio.nombre_consorcio_con_tipo_tooltip_formateado', 'consorcio.nombre_con_id_formateado', 'consorcio.cantidad_ufs_facturables', 'nombre_estado_formateado', 'fecha_edicion_formateada', 'propuesta_consorcio.dias_restantes_formateado', 'descuentos_formateados', 'propuesta_consorcio.periodo_inicio_facturacion_formateado', 'usuario_sales', 'fecha_creacion_formateado', 'usuario_edicion', 'origen_formateado'],
  }),
  computed: {

    /**
     * Computed para calcular el total de UFs acumuladas por los acuerdos comerciales mostrados
     */
    chipTotalizadorUfs() {
      let total_ufs = 0;
      this.acuerdosComercialesFiltrados.forEach((acuerdo_comercial) => {
        if(acuerdo_comercial.tipo_propuesta ===  this.TIPO_ACUERDO_CONSORCIO){
          total_ufs += acuerdo_comercial.consorcio.cantidad_ufs_facturables;
        }
      });

      return total_ufs;
    },

    /**
     * Retorna los acuerdos comerciales filtrados en base a los filtros de la tabla, es decir, aplicandole los filtros de 2do nivel
     * Los filtros son aplicados siempre sobre los AC filtrados por ca-filtro de base.
     * 
     * Estos filtros son dinámicos dependiendo del tipo de busqueda que se realizo:
     * 1. Se filtra por agente
     * 2. Se filtra por estado (opcional: en tipo de busqueda por defecto no se muestra)
     */
    acuerdosComercialesFiltrados() {
      return this.acuerdos_comerciales_filtrados_por_ca_filtro.filter((acuerdo_comercial) => {
        // Filtramos primero por agente
        const filtrar_por_agente = this.filtro_agente === null || acuerdo_comercial.usuario_sales === this.filtro_agente;

        // Si seleccionó el filtro para los expirados, se filtra tanto por agente como por los acuerdos comerciales que esten expirados
        if (this.filtro_estado === TiposEstadosAcuerdoComercial.EXPIRADO) {
          return filtrar_por_agente && acuerdo_comercial.expirada;
        }

        // Si el filtro no es para los expirados, se filtra por agente y por estado
        return (filtrar_por_agente && (this.filtro_estado === null || acuerdo_comercial.estado === this.filtro_estado)
        );
      });
    },

    /**
     * Computed que retorna los headers que se mostrarán en la tabla
     *  - Si el tipo de búsqueda es por defecto, se muestran los headers para la búsqueda por defecto
     *  - Si el tipo de búsqueda no es por defecto y el flag para mostrar las columnas opcionales esta activo, se muestran los headers que incluyen las columnas opcionales caso contrario se retornan los headers sin las columnas opcionales
     */
    headersTabla(){

      if(this.tipo_acuerdo_filtros_primer_nivel === TIPO_BUSQUEDA_ACUERDO_COMERCIAL_POR_DEFECTO) {
        return this.headers_busqueda_ac_por_defecto;
      }

      return this.mostrar_columnas_opcionales ? this.headers_con_columnas_opcionales : this.headers_sin_columnas_opcionales;
    },

    /**
     * Computed que retorna los acuerdos comerciales actualizados que mostrará la tabla
     * Se ejecuta cuando se cambia el estado del loading o se actualiza la lista de acuerdos comerciales 
     * - Si estamos en loading, sera un arreglo vacio.
     * - Si se elimina el último acuerdo comercial
     *    - Debemos accionar ya que al estar condicionado todo el template impide que cuando no hayan ACs se refleje ese cambio en los acuerdos que recibe la tabla.
     */
    acuerdosComercialesActualizados(){
      if(this.loading) {
        return []; // Retornamos un arreglo vacio para que la tabla muestre solo el wording asociado al loading y no haya elementos en la tabla
      }else{
        return this.acuerdos_comerciales.length ? this.acuerdosComercialesFiltrados : [];
      }
    }
  },
  async mounted(){

    // Seteamos los headers de la tabla sin columnas opcionales filtrando aquellos que no son columnas opcionales
    this.headers_sin_columnas_opcionales = await this.headers_con_columnas_opcionales.filter((header) => !header.columna_opcional);

    // Actualizó si tengo que mostrar los headers con columnas opcionales
    await this.actualizarMostrarColumnasOpcionales();

    // Calculo las opciones de los selects
    this.opcionesEstados();
    this.opcionesAgente();
  },
  methods: {
    trimUsuarioSales,

    /**
     * Metodo que emite evento al Event Bus para abrir el sidesheet de detalles e historial (en el tab de detalles activo) con el acuerdo comercial seleccionado
     */
    abrirSidesheetDetalles(acuerdo_comercial_seleccionado) {
      EventBus.$emit('sidesheet-detalles:abrir', acuerdo_comercial_seleccionado);
    },

    /**
     * Metodo que retorna los estados posibles para filtrar los ACs
     * Se filtra las que tienen el estado archivadas a través del atributo referido al plural (actualmente no estan modeladas)
     * Se agrega de manera custom el estado "Todos" con valor null para poder filtrar por todos los estados
     */
    opcionesEstados() {
      this.estados = [
        { key: null, texto: 'Todos' },
        ...ESTADOS_FILTRABLES_PARA_TABLA_ACUERDOS_COMERCIALES
          .filter((estado) => estado.label_plural)
          .map((estado) => ({
            key: estado.key,
            texto: estado.label_plural
          }))
      ];
    },

    /**
     * Metodo que preasigna el agente de ventas con el usuario logueado
     * Busca el usuario logueado en la lista de agentes de ventas
     *  - Si lo encuentra setea por defecto como agente filtrado el usuario logueado
     *  - Caso contrario deja el valor actual
     */
    preAsignarAgente() {
      if(this.$store.getters.user && this.$store.getters.user.email) {
        const usuario_logueado = this.$store.getters.user.email;

        const existe_usuario_logueado_en_lista_agentes_de_ventas = this.usuarios_agente.find(usuario => usuario.valor_original === usuario_logueado);

        if(existe_usuario_logueado_en_lista_agentes_de_ventas) {
          this.filtro_agente = usuario_logueado;
        }
      }
    },

    /**
     * Metodo para las opciones de agente, crea un arreglo con el nombre formateado y el mail original para el filtrado
     * Agrega la opción de "Todos", con el valor de null para el filtrado
     * Además preasigna el agente seleccionado con el usuario logueado
     */
    opcionesAgente() {
      let usuarios = [];

      // Los usuarios de sales los obtengo del store de metadata
      this.$store.getters[METADATA_GETTER_USUARIOS_SALES].forEach((agente_ventas) => {
        let usuario = { nombre_formateado: trimUsuarioSales(agente_ventas), valor_original: agente_ventas };
        usuarios.push(usuario);
      });

      usuarios.unshift({nombre_formateado: 'Todos', valor_original: null}); // Agrego el filtro para Todos
      this.usuarios_agente = usuarios; // Asigno el resultado a la variable para mostrarlo en el filtro

      this.preAsignarAgente();
    },

    /**
     * Actualizá el flag que indicá si las columnas opcionales se deben ocultar en la tabla
     * Las columnas opcionales se mostrarán si como minimo un acuerdo comercial cumple con todas las siguientes condiciones:
     *  - No es del tipo de acuerdo a la administración
     *  - No es un estado terminador
     *  - No esta expirado
     * 
     * Caso contrario no se mostrarán
     */
    async actualizarMostrarColumnasOpcionales() {
      this.mostrar_columnas_opcionales = await this.acuerdos_comerciales.some((acuerdo_comercial) =>
        acuerdo_comercial.tipo_propuesta && acuerdo_comercial.tipo_propuesta !== TipoAcuerdo.ADMINISTRACION &&
        acuerdo_comercial.estado && !ESTADO_ACUERDO_COMERCIAL_TERMINADORES.includes(acuerdo_comercial.estado) &&
        !acuerdo_comercial.expirada
      );
    },

    /**
     * Metodo que reinicia los filtros de segundo nivel, osea los filtros de la tabla
     * Reiniciamos los acuerdos comerciales que se muestran y los headers de la tabla (mostrando o no las columnas opcionales)
     * Además setea el agente por defecto al usuario logueado
     */
    reiniciarFiltros() {
      this.filtro_agente = null;
      this.filtro_estado = null;
      this.filtro_tabla = '';
      this.actualizarMostrarColumnasOpcionales();
      this.preAsignarAgente();
    },
  },

};
</script>

<style lang="scss">
#tabla-acuerdos-comerciales{
  background-color: color(fondo-claro);
  padding: 10px;

  // Para evitar desbordamiento del footer
  .ca-tabla-footer  {
    margin-left: auto;
    margin-right: auto;
  }

  .icono-tipo-consorcio {
    color: color(gris);
    font-variation-settings:
      'FILL' 1,
      'wght' 400,
      'GRAD' 0,
      'opsz' 24
  }

  .label-dias-restantes-danger {
    color: color(rojo-error);
    font-weight: 700;
  }

  .chip-totalizador {
    height: 26px;
    margin-left: 0;

    .v-chip__content {
      padding-left: 10px;
      padding-right: 10px;
      background-color: color(dark);
      color: color(blanco);
    }
    &--texto-oscuro {
     font-weight: 700;
     margin-left: 2px;
    }
    &--deshabilitado {
      .v-chip__content {
        background-color: color(gris-deshabilitado) !important;
      }
    }
  }

  .texto-mostrado-desbordamiento {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .td-columna-estado:hover{
    cursor: default;
  }

  .filtro-acuerdo-comercial {
    height: 50px;

    &--agentes {
      width: 150px;
    }

    &--estados {
      width: 120px;
      .v-menu__content {
        max-width: none;
      }
    }
  }

  .ca-filtro {
    width: 265px;

    .ca-filtro-textfield {
      margin: 0 !important;
    }
  }

  tr:hover {
    background-color: color(gris-claro) !important;
  }

  th{
    &.col-acciones {
      width: 42px;
    }
    &.col-administracion {
      width: 295px;
    }
    &.col-consorcio {
      width: 295px;
    }
    &.col-ufs {
      width: 80px;
    }
    &.col-estado {
      width: 200px; // Contemplamos el caso en que haya mas de un estado
    }
    &.col-ultima-actualizacion {
      width: 105px;
    }
    &.col-restan {
      width: 83px;
    }
    &.col-descuentos {
      width: 250px;
    }
    &.col-relleno {
      width: 100%; // Se expande para ocupar el espacio disponible de las columnas ocultas
    }
    &.col-inicio {
      width: 82px;
    }
    &.col-creador {
      width: 170px;
    }
    &.col-creacion {
      width: 98px;
    }
    &.col-editor {
      width: 170px;
    }
    &.col-origen {
      width: 90px;
    }
    &.header-gris{
      background-color: color(gris-oscuro);
    }
  }

  /**
   * Se pisa los estilos de la tabla para que se adapte al viewport (el max-height es para poner un tope a la tabla, para evitar que se scrollee en la pagina completa)
   * ToDo - Queda hardocodeado el calculo, investigar / mejorar para hacerlo dinámico.
   */
  .v-data-table__wrapper {
    max-height: calc(100vh - 442px);
  }
}
</style>